import { Observation } from "@medplum/fhirtypes";

import { normalizeObservationFormCode } from "@/encounters/components/EncounterLabor/EncounterLaborVersionDiff/structuredMedTherapyDiffConfig";
import { getObservationPreviousVersionTargetId } from "@/encounters/components/EncounterLabor/utils/observation-version-chain";
import { pickObservationNewerByVersionIdThenRecencyThenId } from "@/utils/observation-recency";

export interface ObservationVersionRow {
  currentObservation: Observation;
  previousObservation?: Observation;
}

function chainGroupKey(observation: Observation): string {
  const subject = observation.subject?.reference ?? "";
  const encounter = observation.encounter?.reference ?? "";
  const formCode = normalizeObservationFormCode(observation.code?.coding?.[0]?.code) ?? "";
  return `${formCode}|${observation.effectiveDateTime ?? ""}|${subject}|${encounter}`;
}

/** Guards against malformed previous-version extension cycles in history bundles. */
const MAX_OBSERVATION_VERSION_CHAIN_DEPTH = 64;

function pickLatestObservation(observations: Observation[]): Observation {
  return observations.reduce((newest, candidate) =>
    pickObservationNewerByVersionIdThenRecencyThenId(newest, candidate),
  );
}

/**
 * Indexes group members by observation id.
 */
function buildMemberObservationsById(members: Observation[]): Map<string, Observation> {
  const membersById = new Map<string, Observation>();
  for (const member of members) {
    if (member.id) {
      membersById.set(member.id, member);
    }
  }
  return membersById;
}

/**
 * Maps each group member id to its in-group previous-version parent id, when present.
 */
function buildChildToParentInGroupMap(
  membersById: ReadonlyMap<string, Observation>,
): Map<string, string> {
  const childToParentInGroup = new Map<string, string>();

  for (const [memberId, member] of membersById) {
    const previousVersionTargetId = getObservationPreviousVersionTargetId(member);
    if (previousVersionTargetId && membersById.has(previousVersionTargetId)) {
      childToParentInGroup.set(memberId, previousVersionTargetId);
    }
  }

  return childToParentInGroup;
}

/**
 * Resolves the oldest observation version in a group by following in-group parent links.
 */
function resolveOldestObservationVersionInGroup(
  newestObservationVersion: Observation,
  childToParentInGroup: ReadonlyMap<string, string>,
  observationsById: ReadonlyMap<string, Observation>,
): Observation {
  let oldestObservationVersion = newestObservationVersion;
  const visitedParentIds = new Set<string>();
  let currentId = newestObservationVersion.id;

  for (let depth = 0; depth < MAX_OBSERVATION_VERSION_CHAIN_DEPTH && currentId; depth += 1) {
    const parentId = childToParentInGroup.get(currentId);
    if (!parentId || visitedParentIds.has(parentId)) {
      break;
    }

    visitedParentIds.add(parentId);
    const parent = observationsById.get(parentId);
    if (!parent) {
      break;
    }

    oldestObservationVersion = parent;
    currentId = parentId;
  }

  return oldestObservationVersion;
}

/**
 * Collapses a multi-version group to one row: newest observation vs oldest in-group version.
 */
function collapseObservationVersionGroup(
  group: ObservationVersionRow[],
  observationsById: ReadonlyMap<string, Observation>,
): ObservationVersionRow {
  if (group.length === 1) {
    return group[0];
  }

  const members = group.map((row) => row.currentObservation);
  const membersById = buildMemberObservationsById(members);
  const childToParentInGroup = buildChildToParentInGroupMap(membersById);
  const targetedWithinGroup = new Set(childToParentInGroup.values());

  const newestObservationVersionCandidates = members.filter(
    (member) => member.id && !targetedWithinGroup.has(member.id),
  );
  const newestObservationVersion =
    newestObservationVersionCandidates.length > 0
      ? pickLatestObservation(newestObservationVersionCandidates)
      : pickLatestObservation(members);

  const oldestObservationVersion = resolveOldestObservationVersionInGroup(
    newestObservationVersion,
    childToParentInGroup,
    observationsById,
  );

  const previousForDiff =
    oldestObservationVersion.id !== newestObservationVersion.id
      ? oldestObservationVersion
      : group[0].previousObservation;

  return {
    currentObservation: newestObservationVersion,
    previousObservation: previousForDiff,
  };
}

/**
 * When a flowsheet row is amended multiple times, parent `hasMember` (or flat postpartum lists) can
 * include every Observation version. The amendment view only needs one diff per logical row:
 * compare the oldest version in the fetched chain to the newest observation version.
 */
export function collapseObservationRowsToLatestChain(
  rows: ObservationVersionRow[],
  observationsById: ReadonlyMap<string, Observation>,
): ObservationVersionRow[] {
  const groups = new Map<string, ObservationVersionRow[]>();
  for (const row of rows) {
    const key = chainGroupKey(row.currentObservation);
    const list = groups.get(key) ?? [];
    list.push(row);
    groups.set(key, list);
  }

  return [...groups.values()].map((group) =>
    collapseObservationVersionGroup(group, observationsById),
  );
}
